import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { ThemedSelect } from '../../components/ThemedSelect';
import {
  getMyPendingChange,
  getMyServices,
  updateMe,
} from '../../../lib/professional-api';
import {
  listAddons,
  listCategories,
  listServices,
  type ServiceAddonRow,
  type ServiceCategory,
  type ServiceItem,
} from '../../../lib/booking-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { ProAddonsManager } from '../../components/ProAddonsManager';
import { ProTreatmentsManager } from '../../components/ProTreatmentsManager';

// Module-level catalogue cache, keyed by category id.
//
// The public catalogue (services + add-ons for a category) barely changes,
// yet this page hit BOTH endpoints again on every category switch. Cache it
// for a short TTL so flipping between categories costs zero network, while an
// admin's catalogue edit still lands within a few minutes. `invalidateCatalog`
// is called after a save so the pro immediately sees their own change.
const CATALOG_TTL_MS = 5 * 60 * 1000;
const catalogCache = new Map<
  string,
  { at: number; services: ServiceItem[]; addons: ServiceAddonRow[] }
>();

async function fetchCatalog(categoryId: string) {
  const hit = catalogCache.get(categoryId);
  if (hit && Date.now() - hit.at < CATALOG_TTL_MS) {
    return { services: hit.services, addons: hit.addons };
  }
  const [svcRes, addonRes] = await Promise.all([
    listServices(categoryId),
    listAddons(categoryId),
  ]);
  const entry = {
    at: Date.now(),
    services: svcRes.data ?? [],
    addons: addonRes.data ?? [],
  };
  catalogCache.set(categoryId, entry);
  return { services: entry.services, addons: entry.addons };
}

function invalidateCatalog() {
  catalogCache.clear();
}

type ProSvcRow = {
  service: { id: string; category?: { id: string } | null };
  custom_price: number | null;
  custom_duration_minutes: number | null;
};

type ProAddonRow = {
  addon: { id: string };
  custom_price: number | null;
  custom_block_minutes: number | null;
};

// Per-service draft used by the inline main-services list. Empty
// string for price/duration means "use catalog default" — sent as
// null to the backend.
type ServiceDraft = {
  enabled: boolean;
  price: string;
  duration_minutes: string;
};

// Per-addon draft backing the table toggle. Price/block are seeded for
// reference but no longer editable — the pro only toggles offer on/off.
type AddonDraft = {
  enabled: boolean;
  price: string;
  block_minutes: string;
};

// Shared style for the inline rate / time number inputs. The
// `appearance:textfield` + spin-button resets strip the native up/down
// steppers (Chrome/Safari/Firefox) so the cells read as clean fields
// instead of the cramped, arrow-cluttered default. Width is set per use.
const NUMBER_INPUT_CLASS =
  'h-[38px] rounded-[8px] border border-[#ede8e9] px-2.5 text-[13px] text-right text-[#3a3335] outline-none transition-colors focus:border-[#8e2a2f] disabled:bg-[#f7f5f3] disabled:text-[#bcbabb] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

export default function ProServicesPage() {
  const [loading, setLoading] = useState(true);
  // One save commits BOTH the service toggles and the add-on toggles in a
  // single updateMe call. Previously each section had its own button, so a
  // pro who toggled add-ons but clicked "Save services" lost the add-on
  // changes silently — they never reached the backend, so they vanished on
  // refresh and never showed up in the admin panel.
  const [saving, setSaving] = useState(false);

  // When opened from Account Settings we get a returnTo and show a back
  // link to it. Reached via the top nav there's no state, so no back
  // button appears (consistent with the other top-level nav pages).
  const location = useLocation();
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ?? null;

  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );

  // TREATMENT-flow categories only — the self-managed treatments block
  // can't author hourly SESSION services (Cleaning / Childcare).
  const treatmentCategories = useMemo(
    () =>
      categories
        .filter((c) => c.booking_flow === 'TREATMENT')
        .map((c) => ({ id: c.id, name: c.name })),
    [categories],
  );

  // Catalog rows for the currently selected category.
  const [catalogServices, setCatalogServices] = useState<ServiceItem[]>([]);
  const [catalogAddons, setCatalogAddons] = useState<ServiceAddonRow[]>([]);

  // Pro's saved opt-ins (used to seed drafts on load + on category swap).
  const [proServices, setProServices] = useState<ProSvcRow[]>([]);
  const [proAddons, setProAddons] = useState<ProAddonRow[]>([]);

  // Services the pro PROPOSED in an outstanding (admin-pending) change.
  // While a change is pending, the live profile still has the OLD set, so
  // we overlay the proposed selection — otherwise a just-submitted service
  // shows as OFF and the pro thinks it never saved. The ref is read
  // synchronously by the seeding effect (state hasn't propagated there yet
  // on first load); the state drives the banner + per-row "Pending" badge.
  const pendingServiceIdsRef = useRef<Set<string> | null>(null);
  const [pendingServiceIds, setPendingServiceIds] = useState<Set<string> | null>(
    null,
  );
  // Full proposed services payload (with custom price/duration) from the
  // pending change. Drives the cross-category carry-over in
  // buildServicesPayload so editing one category never drops another
  // category's still-pending edits. Null when nothing is pending.
  const pendingServicesRef = useRef<Array<{
    service_id: string;
    custom_price: number | null;
    custom_duration_minutes: number | null;
  }> | null>(null);
  const [hasPendingChange, setHasPendingChange] = useState(false);

  // Apply an outstanding admin-pending change to local state: the proposed
  // service set (ids + full payload) becomes the working source of truth so
  // the toggles, "Pending approval" badges, banner, and cross-category
  // carry-over all reflect what the pro submitted (not the stale live set).
  const applyPendingChange = (pc: any) => {
    setHasPendingChange(!!pc);
    const proposedSvcs =
      pc && Array.isArray(pc.payload?.services) ? pc.payload.services : null;
    const normalized = proposedSvcs
      ? proposedSvcs
          .filter((s: any) => s?.is_active !== false)
          .map((s: any) => ({
            service_id: s.service_id,
            custom_price: s.custom_price ?? null,
            custom_duration_minutes: s.custom_duration_minutes ?? null,
          }))
      : null;
    pendingServicesRef.current = normalized;
    const idSet = normalized
      ? new Set<string>(normalized.map((s) => s.service_id))
      : null;
    pendingServiceIdsRef.current = idSet;
    setPendingServiceIds(idSet);
  };

  // Working state mirrors the catalog for the selected category. Saves
  // commit everything in the draft.
  const [serviceDrafts, setServiceDrafts] = useState<
    Record<string, ServiceDraft>
  >({});
  const [addonDrafts, setAddonDrafts] = useState<Record<string, AddonDraft>>({});


  // Track which category we last seeded drafts for so we DON'T re-seed
  // (and wipe local edits) every time proServices/proAddons changes
  // after a save. Re-seeding should only happen on initial load or a
  // genuine category switch.
  const lastSeededCategoryRef = useRef<string | null>(null);

  // Per-pro minimum booking value (Hair & Beauty). String for the input;
  // '' = no minimum. Seeded from the pro's profile.
  const [minBooking, setMinBooking] = useState('');

  // ----- initial load: categories + pro's saved data -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [catRes, me, pcRes] = await Promise.all([
          listCategories(),
          // Lean endpoint: services + add-ons + minimum_booking_value only.
          // The full `/professional/me` also drags the weekly availability
          // rows and every portfolio image's presigned URL (tens of KB) —
          // none of which this page renders.
          getMyServices(),
          // Non-blocking: if this fails the page still works, just without
          // the pending overlay.
          getMyPendingChange().catch(() => ({ status: false, data: null })),
        ]);
        if (cancelled) return;

        const allCats = catRes.data ?? [];
        setCategories(allCats);

        // Overlay any admin-pending change so PROPOSED (not-yet-live)
        // services render as selected + "pending" rather than off. The refs
        // are set synchronously (before setProServices) so the seeding
        // effect, which fires on proServices, reads them on this same load.
        const pc = (pcRes as any)?.data ?? null;
        applyPendingChange(pc);

        const myServices: ProSvcRow[] = me?.info?.services ?? [];
        const myAddons: ProAddonRow[] = me?.info?.service_addons ?? [];
        setProServices(myServices);
        setProAddons(myAddons);
        setMinBooking(
          (me?.info as any)?.minimum_booking_value != null
            ? String((me.info as any).minimum_booking_value)
            : '',
        );

        // Pick the pro's "main" category from their first opted-in
        // service. Falls back to the first catalog category so the
        // page is usable even for a brand-new pro.
        const sample = myServices
          .map((s) => (s as any).service)
          .find((s) => s?.category?.id);
        const initial =
          sample?.category?.id ?? (allCats[0]?.id ?? null);
        setSelectedCategoryId(initial);
      } catch (err) {
        if (!cancelled) {
          const msg = apiErrorMessage(err, 'Could not load your services.');
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ----- when the selected category changes, refetch + seed drafts -----
  useEffect(() => {
    if (!selectedCategoryId) {
      setCatalogServices([]);
      setCatalogAddons([]);
      return;
    }
    // Skip if we've already seeded for this category — otherwise every
    // post-save refresh of proServices/proAddons would wipe local
    // drafts the pro is still working on.
    const alreadySeeded =
      lastSeededCategoryRef.current === selectedCategoryId;
    let cancelled = false;
    (async () => {
      try {
        // Cached per category — switching back and forth is now free.
        const { services: svcData, addons: addonData } =
          await fetchCatalog(selectedCategoryId);
        if (cancelled) return;

        const svcList = svcData
          .slice()
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        const addonList = addonData
          .slice()
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        setCatalogServices(svcList);
        setCatalogAddons(addonList);

        if (alreadySeeded) return;

        // Seed service drafts. Pro keeps their saved enabled/override
        // when the row exists, defaults to "off + catalog values"
        // otherwise.
        const proByServiceId = new Map<string, ProSvcRow>();
        for (const r of proServices) {
          if ((r as any).service?.id) {
            proByServiceId.set((r as any).service.id, r);
          }
        }
        // While a change is PENDING admin approval, the proposed selection
        // is the source of truth — so a pending ADD shows ON, a pending
        // REMOVE shows OFF, and pending price/time overrides show too.
        // With no pending change, fall back to the live (already-offered)
        // set. The proposed payload is always the FULL set across every
        // category (buildServicesPayload carries the rest over), so no
        // category's selection is ever hidden.
        const proposedMap = pendingServicesRef.current
          ? new Map(
              pendingServicesRef.current.map((s) => [s.service_id, s]),
            )
          : null;
        const svcDrafts: Record<string, ServiceDraft> = {};
        for (const s of svcList) {
          const existing = proByServiceId.get(s.id);
          const prop = proposedMap?.get(s.id);
          // `proposedMap != null` means a pending change exists → use the
          // proposed state; otherwise use the live state.
          const customPrice = proposedMap
            ? (prop?.custom_price ?? null)
            : (existing?.custom_price ?? null);
          const customDuration = proposedMap
            ? (prop?.custom_duration_minutes ?? null)
            : (existing?.custom_duration_minutes ?? null);
          svcDrafts[s.id] = {
            enabled: proposedMap ? !!prop : !!existing,
            price:
              customPrice != null
                ? String(customPrice)
                : String(s.base_price ?? ''),
            duration_minutes:
              customDuration != null
                ? String(customDuration)
                : String(s.duration_minutes ?? ''),
          };
        }
        setServiceDrafts(svcDrafts);

        // Seed addon drafts.
        const proByAddonId = new Map<string, ProAddonRow>();
        for (const r of proAddons) {
          if ((r as any).addon?.id) {
            proByAddonId.set((r as any).addon.id, r);
          }
        }
        const addDrafts: Record<string, AddonDraft> = {};
        for (const a of addonList) {
          const existing = proByAddonId.get(a.id);
          addDrafts[a.id] = {
            enabled: !!existing,
            price:
              existing?.custom_price != null
                ? String(existing.custom_price)
                : String(a.price ?? ''),
            block_minutes:
              existing?.custom_block_minutes != null
                ? String(existing.custom_block_minutes)
                : String(a.block_minutes ?? ''),
          };
        }
        setAddonDrafts(addDrafts);
        lastSeededCategoryRef.current = selectedCategoryId;
      } catch (err) {
        if (!cancelled) {
          const msg = apiErrorMessage(err, 'Could not load services.');
          toast.error(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCategoryId, proServices, proAddons]);

  // ----- service handlers -----
  const toggleService = (id: string) => {
    setServiceDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], enabled: !prev[id]?.enabled },
    }));
  };

  // Pros set their own rate / time per service. Blank means "use the
  // platform default" — buildServicesPayload only sends a custom value
  // when it differs from the catalog, so leaving a field empty (or equal
  // to the default) persists null and falls back to the catalogue.
  const setServiceField = (
    id: string,
    field: 'price' | 'duration_minutes',
    value: string,
  ) => {
    setServiceDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  // Build the enabled-services payload. Custom price/duration are sent only
  // when they differ from the catalog default (null = "use default").
  const buildServicesPayload = () => {
    const payload: Array<{
      service_id: string;
      custom_price: number | null;
      custom_duration_minutes: number | null;
      is_active: boolean;
    }> = [];
    // Carry over the pro's selections in EVERY OTHER category untouched.
    // The save replaces the whole set, so if we only sent the active
    // category the others would be dropped. Carry over from the EFFECTIVE
    // selection: the proposed (pending) set when a change is awaiting
    // approval, otherwise the live set. Using the pending set here is what
    // stops editing one category from wiping another category's still-
    // pending edits.
    const activeCatalogIds = new Set(catalogServices.map((s) => s.id));
    const effective = pendingServicesRef.current
      ? pendingServicesRef.current
      : proServices.map((r) => ({
          service_id: (r as any).service?.id as string | undefined,
          custom_price: r.custom_price ?? null,
          custom_duration_minutes: r.custom_duration_minutes ?? null,
        }));
    for (const r of effective) {
      if (!r.service_id || activeCatalogIds.has(r.service_id)) continue;
      payload.push({
        service_id: r.service_id,
        custom_price: r.custom_price ?? null,
        custom_duration_minutes: r.custom_duration_minutes ?? null,
        is_active: true,
      });
    }
    // Active category — from the editable drafts.
    for (const svc of catalogServices) {
      const d = serviceDrafts[svc.id];
      if (!d || !d.enabled) continue;
      const priceNum = d.price.trim() === '' ? null : Number(d.price);
      const durationNum =
        d.duration_minutes.trim() === ''
          ? null
          : parseInt(d.duration_minutes, 10);
      payload.push({
        service_id: svc.id,
        custom_price:
          priceNum != null &&
          Number.isFinite(priceNum) &&
          priceNum !== Number(svc.base_price)
            ? priceNum
            : null,
        custom_duration_minutes:
          durationNum != null &&
          Number.isFinite(durationNum) &&
          durationNum !== svc.duration_minutes
            ? durationNum
            : null,
        is_active: true,
      });
    }
    return payload;
  };

  // Build the enabled-add-ons payload. Custom price/block are sent only
  // when they differ from the catalog default (null = "use default").
  const buildAddonsPayload = () => {
    const payload: Array<{
      service_addon_id: string;
      custom_price: number | null;
      custom_block_minutes: number | null;
      is_active: boolean;
    }> = [];
    // Carry over other categories' add-ons untouched (same full-replace
    // reason as services above).
    const activeCatalogIds = new Set(catalogAddons.map((a) => a.id));
    for (const r of proAddons) {
      const addon = (r as any).addon;
      if (!addon?.id || activeCatalogIds.has(addon.id)) continue;
      payload.push({
        service_addon_id: addon.id,
        custom_price: r.custom_price ?? null,
        custom_block_minutes: r.custom_block_minutes ?? null,
        is_active: true,
      });
    }
    // Active category — from the editable drafts. Custom price / block are
    // sent only when they differ from the catalog default (null = default).
    for (const addon of catalogAddons) {
      const d = addonDrafts[addon.id];
      if (!d?.enabled) continue;
      const priceNum = d.price.trim() === '' ? null : Number(d.price);
      const blockNum =
        d.block_minutes.trim() === '' ? null : parseInt(d.block_minutes, 10);
      payload.push({
        service_addon_id: addon.id,
        custom_price:
          priceNum != null &&
          Number.isFinite(priceNum) &&
          priceNum !== Number(addon.price)
            ? priceNum
            : null,
        custom_block_minutes:
          blockNum != null &&
          Number.isFinite(blockNum) &&
          blockNum !== addon.block_minutes
            ? blockNum
            : null,
        is_active: true,
      });
    }
    return payload;
  };

  // Single save for the whole page: commit the service toggles AND the
  // add-on toggles together so neither section's changes can be lost by
  // saving the other. Both section buttons call this.
  const handleSave = async () => {
    if (saving) return;

    // Every rate / time the pro types must be greater than zero. A blank
    // field is allowed (it falls back to the platform default), but an
    // explicit 0, a negative, or garbage is rejected before the API call.
    const positive = (v: string) => v.trim() === '' || Number(v) > 0;
    for (const svc of catalogServices) {
      const d = serviceDrafts[svc.id];
      if (!d?.enabled) continue;
      if (!positive(d.price)) {
        toast.error(`Enter a rate greater than 0 for ${svc.name}.`);
        return;
      }
      if (!positive(d.duration_minutes)) {
        toast.error(`Enter a time greater than 0 for ${svc.name}.`);
        return;
      }
    }
    for (const addon of catalogAddons) {
      const d = addonDrafts[addon.id];
      if (!d?.enabled) continue;
      if (!positive(d.price)) {
        toast.error(`Enter a price greater than 0 for ${addon.name}.`);
        return;
      }
      if (!positive(d.block_minutes)) {
        toast.error(`Enter a block time greater than 0 for ${addon.name}.`);
        return;
      }
    }

    setSaving(true);
    try {
      const isTreatmentCat =
        categories.find((c) => c.id === selectedCategoryId)?.booking_flow ===
        'TREATMENT';
      const res = await updateMe({
        services: buildServicesPayload(),
        service_addons: buildAddonsPayload(),
        // Only treatment (Hair & Beauty) pros set a minimum; don't touch it
        // from a non-treatment category save.
        ...(isTreatmentCat
          ? {
              minimum_booking_value:
                minBooking.trim() === '' ? null : Number(minBooking),
            }
          : {}),
      });
      // The pro just changed their own listing — drop the cached catalogue so
      // the next read is fresh rather than up-to-5-minutes stale.
      invalidateCatalog();
      // A live pro's rate / time / add-on-price edits apply immediately, but
      // adding/removing a service (or a minimum-value change) is held for
      // admin review. Word the confirmation honestly so the pro isn't
      // surprised when a listing change doesn't appear after refresh.
      if (res?.pending_review) {
        toast.success(
          res.partial_applied
            ? 'Pricing updated. Your other changes were submitted for review and go live once approved.'
            : 'Submitted for review — your changes go live once an admin approves them.',
        );
      } else {
        toast.success('Saved.');
      }
      // Refresh both snapshots so a later category swap reseeds correctly.
      const me = await getMe();
      setProServices(me?.info?.services ?? []);
      setProAddons(me?.info?.service_addons ?? []);
      setMinBooking(
        (me?.info as any)?.minimum_booking_value != null
          ? String((me.info as any).minimum_booking_value)
          : '',
      );

      // Reflect the pending-approval state IMMEDIATELY (banner + per-row
      // "Pending approval" badge) instead of only after a page refresh.
      // Re-fetch the outstanding change and re-derive the proposed set,
      // exactly as the initial load does.
      try {
        const pcRes = await getMyPendingChange();
        applyPendingChange((pcRes as any)?.data ?? null);
      } catch {
        /* non-blocking — the pending overlay just stays as it was */
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save your changes.'));
    } finally {
      setSaving(false);
    }
  };

  // ----- addon handlers -----
  const toggleAddon = (id: string) => {
    setAddonDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], enabled: !prev[id]?.enabled },
    }));
  };

  // Pros set their own price / block time per add-on. Blank (or equal to
  // the catalog default) persists null and falls back to the catalogue.
  const setAddonField = (
    id: string,
    field: 'price' | 'block_minutes',
    value: string,
  ) => {
    setAddonDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const ToggleSwitch = ({
    enabled,
    onToggle,
    disabled,
  }: {
    enabled: boolean;
    onToggle: () => void;
    disabled?: boolean;
  }) => (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={`relative w-[40px] h-[22px] rounded-full transition-colors duration-200 shrink-0 ${enabled ? 'bg-[#8e2a2f]' : 'bg-[#908e8e]/30'} disabled:opacity-60`}
    >
      <div
        className={`absolute top-[2px] w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-transform duration-200 ${enabled ? 'translate-x-[20px]' : 'translate-x-[2px]'}`}
      />
    </button>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col font-['DM_Sans',sans-serif]">
      <HeaderPro />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-6 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            {returnTo ? (
              <Link
                to={returnTo}
                aria-label="Back"
                className="w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
              >
                <span className="material-icons text-[#908e8e] text-[18px]">
                  chevron_left
                </span>
              </Link>
            ) : null}
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[24px] leading-[1.2] text-[#8e2a2f]">
              Services
            </h1>
          </div>

          {/* Category selector — swap to load that category's catalog */}
          <div className="flex items-center gap-3">
            <span className="text-[13px] text-[#908e8e] font-bold">
              Main category
            </span>
            <ThemedSelect
              value={selectedCategoryId ?? ''}
              onChange={(v) => setSelectedCategoryId(v || null)}
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
              buttonClassName="h-[40px] min-w-[160px]"
            />
          </div>
        </div>

        {categories.find((c) => c.id === selectedCategoryId)?.booking_flow ===
        'TREATMENT' ? (
          <div className="mb-8 rounded-[12px] border border-[#ede8e9] bg-white p-4">
            <label className="block text-[14px] font-bold text-[#3a3335] mb-1">
              Minimum booking value
            </label>
            <p className="text-[12px] text-[#908e8e] mb-3">
              Clients can&apos;t book you for less than this total (services +
              add-ons). It&apos;s shown on your profile before they book. Leave
              blank for no minimum.
            </p>
            <div className="flex items-center gap-2">
              <span className="text-[#908e8e] text-[15px]">£</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={minBooking}
                onChange={(e) => setMinBooking(e.target.value)}
                placeholder="e.g. 30"
                className="h-[40px] w-[160px] rounded-[10px] border border-[#ede8e9] px-3 text-[14px] outline-none focus:border-[#8e2a2f]"
              />
            </div>
          </div>
        ) : null}

        {loading ? (
          <p className="text-sm text-[#908e8e]">Loading services…</p>
        ) : (
          <>
            {/* Pending-approval banner — shown whenever the pro has an
                outstanding change in the admin review queue. Without this
                (and the per-row badge + proposed-state toggles below) a
                just-submitted service rendered as OFF, so it looked like
                the pro had never selected it. */}
            {hasPendingChange ? (
              <div className="mb-6 rounded-[10px] border border-[#f3d6a0] bg-[#fff7e6] px-4 py-3">
                <p className="font-bold text-[14px] text-[#9a6700]">
                  Changes awaiting admin approval
                </p>
                <p className="mt-0.5 text-[13px] text-[#9a6700]">
                  You&apos;ve submitted updates to your services. They&apos;re
                  shown below marked{' '}
                  <span className="font-semibold">Pending approval</span> and go
                  live on your profile once our team approves them.
                </p>
              </div>
            ) : null}

            {/* ---------------- Main services ---------------- */}
            <div className="mb-12">
              <div className="bg-[#f2eee2] px-6 py-3">
                <p className="font-bold text-[14px] text-[#8e2a2f]">
                  Main services
                </p>
              </div>

              {catalogServices.length === 0 ? (
                <p className="px-6 py-6 text-sm text-[#908e8e]">
                  No services configured for this category yet.
                </p>
              ) : (
                <>
                  <div className="divide-y divide-[#eee]">
                    {catalogServices.map((svc) => {
                      const d = serviceDrafts[svc.id];
                      if (!d) return null;
                      const perHour =
                        svc.pricing_unit === 'PER_HOUR' ? '/hr' : '';
                      // This row is "pending" when the proposed selection
                      // (from the admin-pending change) differs from what's
                      // currently live — so the pro sees the toggle in its
                      // proposed state AND why it isn't live yet.
                      const liveEnabled = proServices.some(
                        (r) => (r as any).service?.id === svc.id,
                      );
                      const rowPending =
                        pendingServiceIds != null &&
                        pendingServiceIds.has(svc.id) !== liveEnabled;
                      return (
                        <div
                          key={svc.id}
                          className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-6 py-4"
                        >
                          <div className="flex-1 min-w-0">
                            <p
                              className={`font-bold text-[15px] tracking-[-0.1px] ${d.enabled ? 'text-[#3a3335]' : 'text-[#908e8e]'}`}
                            >
                              {svc.name}
                              {rowPending ? (
                                <span className="ml-2 inline-flex items-center rounded-full bg-[#fff7e6] px-2 py-0.5 text-[11px] font-semibold text-[#9a6700] ring-1 ring-inset ring-[#f3d6a0] align-middle">
                                  Pending approval
                                </span>
                              ) : null}
                            </p>
                          </div>

                          {/* Pros set their own rate AND time per service.
                              A blank field (or one left at the platform
                              default) falls back to the catalogue value on
                              save. Inputs lock when the service is toggled
                              off — you set the rate/time for what you offer. */}
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="flex items-center gap-1">
                              <span className="text-[13px] text-[#908e8e]">£</span>
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                inputMode="decimal"
                                value={d.price}
                                onChange={(e) =>
                                  setServiceField(svc.id, 'price', e.target.value)
                                }
                                disabled={!d.enabled}
                                placeholder={String(svc.base_price ?? '')}
                                aria-label={`${svc.name} rate`}
                                className={`w-[84px] ${NUMBER_INPUT_CLASS}`}
                              />
                              {perHour ? (
                                <span className="text-[12px] text-[#908e8e]">
                                  /hr
                                </span>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min={0}
                                step="5"
                                inputMode="numeric"
                                value={d.duration_minutes}
                                onChange={(e) =>
                                  setServiceField(
                                    svc.id,
                                    'duration_minutes',
                                    e.target.value,
                                  )
                                }
                                disabled={!d.enabled}
                                placeholder={String(svc.duration_minutes ?? '')}
                                aria-label={`${svc.name} duration in minutes`}
                                className={`w-[72px] ${NUMBER_INPUT_CLASS}`}
                              />
                              <span className="text-[12px] text-[#908e8e]">min</span>
                            </div>
                            <ToggleSwitch
                              enabled={d.enabled}
                              onToggle={() => toggleService(svc.id)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="px-6 py-4 flex justify-end">
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="h-[44px] px-6 rounded-[8px] bg-[#8e2a2f] text-white text-[14px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
                    >
                      {saving ? 'Saving…' : 'Save services & add-ons'}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* ---------------- Other services (addons) ---------------- */}
            <div>
              <div className="bg-[#f2eee2] px-6 py-3 grid grid-cols-[2fr_1fr_1fr_auto] gap-4 items-center">
                <p className="font-bold text-[14px] text-[#8e2a2f]">
                  Other services
                </p>
                <p className="font-bold text-[14px] text-[#8e2a2f] text-center">
                  Per block
                </p>
                <p className="font-bold text-[14px] text-[#8e2a2f] text-center">
                  Price
                </p>
                <p className="font-bold text-[14px] text-[#8e2a2f] text-right pr-2">
                  Action
                </p>
              </div>

              {catalogAddons.length === 0 ? (
                <p className="px-6 py-6 text-sm text-[#908e8e]">
                  No add-ons configured for this category yet.
                </p>
              ) : (
                <div className="divide-y divide-[#eee]">
                  {catalogAddons.map((addon) => {
                    const d = addonDrafts[addon.id] ?? {
                      enabled: false,
                      price: String(addon.price),
                      block_minutes: String(addon.block_minutes),
                    };
                    return (
                      <div
                        key={addon.id}
                        className="grid grid-cols-[2fr_1fr_1fr_auto] gap-4 px-6 py-4 items-center"
                      >
                        <p
                          className={`font-bold text-[14px] ${d.enabled ? 'text-[#3a3335]' : 'text-[#908e8e]'}`}
                        >
                          {addon.name}
                        </p>
                        {/* Per block (minutes) — editable. Locks when the
                            add-on is toggled off. */}
                        <div className="flex justify-center">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min={0}
                              step="5"
                              inputMode="numeric"
                              value={d.block_minutes}
                              onChange={(e) =>
                                setAddonField(
                                  addon.id,
                                  'block_minutes',
                                  e.target.value,
                                )
                              }
                              disabled={!d.enabled}
                              placeholder={String(addon.block_minutes ?? '')}
                              aria-label={`${addon.name} block minutes`}
                              className={`w-[72px] ${NUMBER_INPUT_CLASS}`}
                            />
                            <span className="text-[12px] text-[#908e8e]">m</span>
                          </div>
                        </div>
                        {/* Price — editable. */}
                        <div className="flex justify-center">
                          <div className="flex items-center gap-1">
                            <span className="text-[13px] text-[#908e8e]">£</span>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              inputMode="decimal"
                              value={d.price}
                              onChange={(e) =>
                                setAddonField(addon.id, 'price', e.target.value)
                              }
                              disabled={!d.enabled}
                              placeholder={String(addon.price ?? '')}
                              aria-label={`${addon.name} price`}
                              className={`w-[84px] ${NUMBER_INPUT_CLASS}`}
                            />
                          </div>
                        </div>
                        <div className="justify-self-end">
                          <ToggleSwitch
                            enabled={d.enabled}
                            onToggle={() => toggleAddon(addon.id)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {catalogAddons.length > 0 ? (
                <div className="px-6 py-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="h-[44px] px-6 rounded-[8px] bg-[#8e2a2f] text-white text-[14px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
                  >
                    {saving ? 'Saving…' : 'Save services & add-ons'}
                  </button>
                </div>
              ) : null}
            </div>
          </>
        )}

        <ProTreatmentsManager categories={treatmentCategories} />

        <ProAddonsManager />
      </main>

      <Footer />

    </div>
  );
}
